import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './shared';

function scheduleColor(rating) {
  if (!rating) return '#6b8dad';
  if (/on.?track/i.test(rating) || rating.includes('🟢')) return '#059669';
  if (/behind/i.test(rating)   || rating.includes('🔴')) return '#dc2626';
  return '#d97706';
}

function trendIcon(trend) {
  if (!trend) return '➡️';
  if (/acceler/i.test(trend) || trend.includes('📈')) return '📈';
  if (/slow/i.test(trend)    || trend.includes('📉')) return '📉';
  return '➡️';
}

export default function RoadmapCard({ data }) {
  const { t } = useTranslation();
  const color = scheduleColor(data.onSchedule);
  return (
    <Card title={t('roadmapCard.title')} icon="🗺">
      {data.onSchedule && (
        <div style={{ ...s.statusBanner, borderColor: color + '40', background: color + '10' }}>
          <span style={{ ...s.statusDot, background: color }} />
          <span style={{ ...s.statusText, color }}>{data.onSchedule.replace(/🟢|🟡|🔴/g, '').trim()}</span>
        </div>
      )}

      <div style={s.timeline}>
        {data.delivered.map((m, i) => (
          <div key={`d${i}`} style={s.milestone}>
            <span style={{ ...s.milestoneIcon, background: 'rgba(5,150,105,0.12)' }}>✅</span>
            <span style={s.milestoneText}>{m}</span>
          </div>
        ))}
        {data.delayed.map((m, i) => (
          <div key={`w${i}`} style={s.milestone}>
            <span style={{ ...s.milestoneIcon, background: 'rgba(217,119,6,0.12)' }}>⚠️</span>
            <span style={{ ...s.milestoneText, color: '#d97706' }}>{m}</span>
          </div>
        ))}
        {data.abandoned.map((m, i) => (
          <div key={`a${i}`} style={s.milestone}>
            <span style={{ ...s.milestoneIcon, background: 'rgba(220,38,38,0.12)' }}>❌</span>
            <span style={{ ...s.milestoneText, color: '#dc2626' }}>{m}</span>
          </div>
        ))}
      </div>

      {data.next.length > 0 && (
        <div style={s.nextSection}>
          <div style={s.nextLabel}>{t('roadmapCard.upcomingMilestones')}</div>
          {data.next.map((q, i) => (
            <div key={i} style={s.quarterRow}>
              <span style={s.quarterTag}>{q.quarter}</span>
              <span style={s.quarterItems}>{q.items}</span>
            </div>
          ))}
        </div>
      )}

      {Object.keys(data.velocity).length > 0 && (
        <div style={s.velocity}>
          <div style={s.velLabel}>{t('roadmapCard.devVelocity')}</div>
          <div style={s.velGrid}>
            {[
              { label: 'Commits (30d)', value: data.velocity.commits },
              { label: t('roadmapCard.lastRelease'), value: data.velocity.lastRelease },
              { label: 'Contributors',  value: data.velocity.contributors },
              { label: t('roadmapCard.trend'), value: data.velocity.trend ? `${trendIcon(data.velocity.trend)} ${data.velocity.trend.replace(/📈|➡️|📉/g, '').trim()}` : null },
            ].filter(v => v.value).map(v => (
              <div key={v.label} style={s.velItem}>
                <span style={s.velItemLabel}>{v.label}</span>
                <span style={s.velItemValue}>{v.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

const s = {
  statusBanner: { display: 'flex', alignItems: 'center', gap: 9, borderRadius: 9, padding: '9px 14px', border: '1px solid', marginBottom: 14 },
  statusDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  statusText: { fontSize: 12, fontWeight: 700 },
  timeline: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  milestone: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  milestoneIcon: { width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 },
  milestoneText: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, paddingTop: 3 },
  nextSection: {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '12px 14px', marginBottom: 12,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  nextLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 },
  quarterRow: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  quarterTag: {
    fontSize: 11, fontWeight: 700, color: '#1f58a4',
    background: 'rgba(31,88,164,0.1)', border: '1px solid rgba(31,88,164,0.25)',
    borderRadius: 6, padding: '2px 9px', flexShrink: 0, whiteSpace: 'nowrap',
  },
  quarterItems: { fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5 },
  velocity: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' },
  velLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 },
  velGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px' },
  velItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  velItemLabel: { fontSize: 10, color: 'var(--text-dim)', fontWeight: 600 },
  velItemValue: { fontSize: 12, color: 'var(--text)', fontWeight: 600 },
};
